import React, {useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {ReportHeaderView} from './viewer/ReportHeaderView'
import {formatDate} from '../components/datepicker'
import {ReportSignatureView} from './viewer/ReportSignatureView'
import {Button} from '@startlibs/components'
import {getKeyImageNameOrPlaceholder, isoStrToDate} from '../lib'
import {getLanguage} from "../components/getLanguage";
import {usePrevious, useRefState, useToggle} from "@startlibs/core";
import {useOnLoadImages} from "./viewer/useOnLoadImages";
import {
  clearPagination, fixLiRenderBug,
  getAddendumAction,
  getBlockInsertionPoint,
  getEmptySpace, getImagePosition,
  getLastDiff,
  getLastPageFiller,
  getLineHeight,
  getPageBreak,
  getSeparators
} from "./utils";
import {smoothScroll} from "@startlibs/utils";
import {RichTextDisplay} from "../components/slateEditor/RichTextDisplay";


const KEY_IMAGE_PPI = 25.4 / 100;
const BROWSER_PPI = 25.4 / 96;
const PAPER_HORIZONTAL_MARGINS = 40;
const IMAGE_BORDER = 2;
const PAGE_HEIGHT = 297; // A4 page height in mm
const PAGE_WIDTH = 210; // A4 page width in mm

const PAPER_SIZES = {
  A4: [8.27, 11.69],
  LETTER: [8.5, 11]
}

const KeyImagePrint = styled.img`
  ${props => {
    const { paperWidth, width, height, backgroundImage } = props;

    const maxWidth = PAGE_WIDTH - PAPER_HORIZONTAL_MARGINS;
    const maxHeight = PAGE_HEIGHT - (PAPER_HORIZONTAL_MARGINS * 2.4);

    const aspectRatio = width / height;
    let adjustedWidth, adjustedHeight;

    // Determine adjusted dimensions based on aspect ratio
    if (maxWidth / aspectRatio <= maxHeight) {
      adjustedWidth = maxWidth;
      adjustedHeight = maxWidth / aspectRatio;
    } else {
      adjustedHeight = maxHeight;
      adjustedWidth = maxHeight * aspectRatio;
    }

    // Ensure we do not scale up beyond original dimensions
    if (adjustedWidth > width * KEY_IMAGE_PPI) {
      adjustedWidth = width * KEY_IMAGE_PPI;
      adjustedHeight = adjustedWidth / aspectRatio;
    }
    if (adjustedHeight > height * KEY_IMAGE_PPI) {
      adjustedHeight = height * KEY_IMAGE_PPI;
      adjustedWidth = adjustedHeight * aspectRatio;
    }

    return `
      width: ${adjustedWidth}mm;
      height: ${adjustedHeight}mm;
    `;
  }}
  background-size: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-image: ${props => props.backgroundImage};
  position: relative;
`;

const PageResizerContainer = styled.div`
  overflow: hidden;
`

const getBackgroundKeyImage = (keyImage, getResource) => keyImage.file ? `${URL.createObjectURL(keyImage.file)}` : `${getResource(keyImage.resourceUID)}`
const getAddendumPagePrefix = (i) => "Addendum #"+i + " - "

export const Preview = (props) => {

  const [lang,setLang] = useState()

  useEffect(() => {
    setLang(null)
    const languageKey = props.reportSettings.layout.language
    getLanguage(languageKey).then((lang) => setLang(lang[languageKey]))
  },[props.reportSettings.layout.language])

  if (!lang) {
    return null
  }
  return <LocalizedPreview lang={lang} {...props} />
}
const LocalizedPreview = (
  {
    isPdfPrinter,
    reportSettings,
    addendums,
    reportSignatures,
    reportKeyImages,
    getResource,
    sharedReportView,
    editAddendum,
    isEditingAddendum,
    setImagesLoaded,
    finished,
    Fill,
    lastSaved,
    hideKeyImages,
    patientName,
    reportTitle,
    reportBasicInfos,
    reportFields,
    availableSignatures,
    lang,
    paperSize =  reportSettings.layout?.paperSize || PAPER_SIZES.A4
  }) => {

  const prevAddendums = usePrevious(addendums)
  const reportContainerRef = useRef()
  const headerContainerRef = useRef()
  const fieldsContainerRef = useRef()
  const signaturesContainerRef = useRef()
  const reportPaginationPageFooterRef = useRef()
  const addendumsContainerRef = useRef()
  const lastPageFillerRef = useRef()
  const reportKeyImagesContainerRef = useRef()
  const reportFooterRef = useRef()
  const footerFillerRef = useRef()
  const compactHeaderContainerRef = useRef()

  const activeScale = useRefState()
  const totalHeight = useRefState()

  const imagesLoaded = useOnLoadImages(reportContainerRef);
  const {organization, layout} = reportSettings
  const {lastPageFooter} = layout


  const resize = () => {
    if (!reportContainerRef.current || isPdfPrinter || !imagesLoaded) return;
    const resizerContainer = reportContainerRef.current.parentNode
    const diff = resizerContainer.scrollWidth - resizerContainer.clientWidth
    if (activeScale.get() === 1 && diff <= 0) return;
    const pageWidth = Math.round(paperSize[0] * 96)
    if (totalHeight.get() === null) {
      totalHeight.set(parseInt(window.getComputedStyle(resizerContainer).height))
    }
    activeScale.set(Math.min(resizerContainer.clientWidth / (reportContainerRef.current.clientWidth+2), 1))
    reportContainerRef.current.style.transform = "scale(" + activeScale.get() + ")";
    reportContainerRef.current.style.transformOrigin = "left top";
    resizerContainer.style.height = (totalHeight.get() * activeScale.get()) + "px"
  }


  const addPreviewPagination = () => {

    footerFillerRef.current.style.height = 0
    reportContainerRef.current.style.width = Math.round(paperSize[0] * 96) + 'px'
    reportContainerRef.current.style.height = (Math.round(paperSize[1] * 96) +
      parseInt(getComputedStyle(reportContainerRef.current).borderTopWidth, 10) +
      parseInt(getComputedStyle(reportContainerRef.current).borderBottomWidth, 10)) + 'px'
    const {height: paperHeight, top: containerTopWithBorder} = reportContainerRef.current.getBoundingClientRect()
    const containerTop = containerTopWithBorder
    const reportContainerStyle = window.getComputedStyle(reportContainerRef.current)
    const contentMarginTop = (parseFloat(reportContainerStyle.paddingTop, 10) || 0) + (parseFloat(reportContainerStyle.borderTop, 10) || 0)
    const contentMarginBottom = (parseFloat(reportContainerStyle.paddingBottom, 10) || 0) + (parseFloat(reportContainerStyle.borderBottom, 10) || 0)
    const contents = Array.prototype.slice.call(fieldsContainerRef.current.querySelectorAll('.breakable-field'))
    const lineHeight = getLineHeight(contents)
    const minBreakSize = lineHeight * 3
    const minContentSizeToBreak = lineHeight * 6

    const compactHeaderHeight = compactHeaderContainerRef.current.offsetHeight

    const contentPageHeight = paperHeight - contentMarginBottom - contentMarginTop - compactHeaderHeight
    const reportFooterHeight = lastPageFooter ? reportFooterRef.current.offsetHeight : 0
    const contentLastPageHeight = contentPageHeight - reportFooterHeight

    const top = containerTop + contentMarginTop + compactHeaderHeight


    const {top: lastContentsTop, separators} = getSeparators(contents, top, {
      contentPageHeight,
      minBreakSize,
      fakeHeaderSpacing: compactHeaderHeight,
      contentLastPageHeight: reportSignatures.length ? contentPageHeight : contentLastPageHeight,
      canBeLastPage: !reportSignatures.length,
      lastPageIsCover: true,
      minContentSizeToBreak
    }, 0, true)

    const {top: lastSignatureTop, separators: signaturesSeparators} = getSeparators(
      Array.prototype.slice.call(signaturesContainerRef.current.children),
      lastContentsTop,
      {
        contentPageHeight,
        contentLastPageHeight,
        fakeHeaderSpacing: compactHeaderHeight,
        minBreakSize: contentPageHeight,
        canBeLastPage: true,
        minContentSizeToBreak: contentPageHeight
      },
      getLastDiff(separators), separators.length === 0
    )

    const preFooterSeparators = separators.concat(signaturesSeparators)

    const footerDiff = Math.floor(
      lastSignatureTop + contentPageHeight -
      footerFillerRef.current.getBoundingClientRect().bottom -
      getLastDiff(preFooterSeparators)
    )
    const {top: lastKeyImageTop, separators: keyImageSeparators} = getSeparators(
      Array.prototype.slice.call(reportKeyImagesContainerRef.current.children),
      lastSignatureTop,
      {
        contentPageHeight,
        contentLastPageHeight: contentPageHeight,
        minBreakSize: contentPageHeight,
        minContentSizeToBreak: contentPageHeight
      },
      footerDiff + getLastDiff(preFooterSeparators), false
    )

    const preAddendumSeparators = preFooterSeparators.concat(keyImageSeparators)

    const {separators: addendumSeparators, top: addendumsLastTop} = Array.prototype.slice.call(addendumsContainerRef.current.children)
      .reduce((acc, current, addendumIndex) => {
        const {separators, top} = getSeparators(
          Array.prototype.slice.call(current.querySelectorAll('.breakable-field,.field-content-block,.always-break-here')),
          acc.top,
          {
            contentPageHeight,
            contentLastPageHeight: contentPageHeight,
            minBreakSize: contentPageHeight,
            minContentSizeToBreak: contentPageHeight
          },
          acc.lastContentDiff === undefined ? contentPageHeight : acc.lastContentDiff, false
        )

        const lastPageDiff = Math.floor(
          top + contentPageHeight -
          getLastPageFiller(current).getBoundingClientRect().bottom -
          getLastDiff(separators)
        )

        const groupedSeparators = separators.map((v,i) => ({...v,groupSize:separators.length,page:i,addendum:addendums[addendumIndex]}))
        const addendumSeparators = [...acc.separators, ...groupedSeparators.slice(0, -1).concat(groupedSeparators.slice(-1).map((separator, i) => i === 0 ? {
          ...separator,
          lastPageDiff
        } : separator))]
        return {separators: addendumSeparators, top}
      }, {separators: [], top: lastKeyImageTop})



    if (!addendumSeparators.length) {
      addendumsContainerRef.current.style.display = 'none'
    }

    const allSeparators = preAddendumSeparators
    if (reportKeyImages.length && !hideKeyImages) {
      const lastPageDiff = Math.floor(
        lastKeyImageTop + contentPageHeight -
        lastPageFillerRef.current.getBoundingClientRect().bottom -
        getLastDiff(preAddendumSeparators)
      )
      lastPageFillerRef.current.style.height = (lastPageDiff) + 'px'
    } else {
      reportKeyImagesContainerRef.current.style.display = 'none'
      lastPageFillerRef.current.style.display = 'none'
    }
    const footerFillerHeight = (footerDiff + reportFooterHeight + (lastPageFooter ? -24 : 42))
    footerFillerRef.current.style.height = footerFillerHeight + 'px'
    if (footerFillerHeight < 0 && !lastPageFooter) {
      footerFillerRef.current.style.marginTop = (footerFillerHeight)+'px'
    }


    reportPaginationPageFooterRef.current.setAttribute('data-pages', addendumSeparators.length ? addendumSeparators.slice(-1)[0].groupSize : (preAddendumSeparators.length)+1)
    reportPaginationPageFooterRef.current.setAttribute('data-pages-prefix', addendums.length ? getAddendumPagePrefix(addendums.length) : "")

    addendumSeparators.slice().reverse().forEach(({diff, position, node, footerDiff, groupSize, addendum, lastPageDiff}, i,arr) => {
      const nextItem = arr[i+1]
      const page = nextItem ? nextItem.page : preAddendumSeparators.length
      const totalPages = nextItem ? (nextItem.groupSize-1) : preAddendumSeparators.length

      const addendumAction = getAddendumAction(node)
      const clonedAddendumAction = addendumAction && addendumAction.cloneNode(true)
      if (clonedAddendumAction) {
        clonedAddendumAction.style.display = "block"
        clonedAddendumAction.className = clonedAddendumAction.className + " remove-on-clear"
        if (clonedAddendumAction.className.indexOf("not-editable")<0) {
          clonedAddendumAction.addEventListener("click",() => editAddendum(addendum))
        }
      }
      const addendumNumber = addendums.indexOf(nextItem && nextItem.addendum)
      const pagePrefix = addendumNumber >= 0 ? getAddendumPagePrefix(addendumNumber+1) : ""
      if (position !== undefined) {
        const range = document.createRange()
        range.setStart(node, position)
        range.setEnd(node, position)
        if (clonedAddendumAction) {
          range.insertNode(clonedAddendumAction)
        }
        range.insertNode(getPageHeader())
        range.insertNode(getPageBreak(page, totalPages, pagePrefix))
        range.insertNode(getEmptySpace(diff))
      } else {
        const target = getBlockInsertionPoint(node)
        target.parentNode.insertBefore(getEmptySpace(diff), target)
        target.parentNode.insertBefore(getPageBreak(page, totalPages, pagePrefix), target)
        target.parentNode.insertBefore(getPageHeader(), target)
        if (clonedAddendumAction) {
          target.parentNode.insertBefore(clonedAddendumAction, target)
        }
      }
      if (lastPageDiff > 0) {
        getLastPageFiller(node).style.height = (lastPageDiff) + "px"
      }
    })


    preAddendumSeparators.reverse().forEach(({diff, position, node, footerDiff}, i) => {
      const page = allSeparators.length - 1 - i
      if (position !== undefined) {
        const range = document.createRange()
        range.setStart(node, position)
        range.setEnd(node, position)
        range.insertNode(getPageHeader())
        range.insertNode(getPageBreak(page, allSeparators.length))
        range.insertNode(getEmptySpace(diff))
      } else {
        const target = getBlockInsertionPoint(node)
        target.parentNode.insertBefore(getEmptySpace(diff), target)
        target.parentNode.insertBefore(getPageBreak(page, allSeparators.length), target)
        target.parentNode.insertBefore(getPageHeader(), target)
      }
    })


    if (allSeparators.length === 0) {
      reportFooterRef.current.style.display = 'none'
    }
  }
  const getPageHeader = () => {
    const clone = compactHeaderContainerRef.current.cloneNode(true)
    const height = compactHeaderContainerRef.current.offsetHeight
    const container = document.createElement('div')
    container.style.height = height + 'px'
    container.className = 'report-header-fix-container'
    container.appendChild(clone)
    return container
  }

  useEffect(() => {
    resize()
  }, []);

  useEffect(() => {
    if (!imagesLoaded) { return }
    const createLayout = () => {
      reportPaginationPageFooterRef.current.className = 'report-page-footer'
      compactHeaderContainerRef.current.style.display = 'block'
      lastPageFillerRef.current.style.height = '0'
      footerFillerRef.current.style.height = '0'
      footerFillerRef.current.style.marginTop = ''
      reportFooterRef.current.style.display = 'block'
      addendumsContainerRef.current.style.display = 'block'

      clearPagination(reportContainerRef.current)

      try {
        addPreviewPagination()
        fixLiRenderBug(reportContainerRef.current)
        reportContainerRef.current.classList.add('ready-for-print')
      } catch (e) {
        console.error(e)
        reportPaginationPageFooterRef.current.className = 'report-page-footer-failed'
        clearPagination(reportContainerRef.current)
      }
      const pageBreaks = [...reportContainerRef.current.querySelectorAll(".page-break")].map(pb =>
        pb.getBoundingClientRect().bottom
      )
      setImagesLoaded([...document.querySelectorAll("img")].map(img =>
        ({
          src: img.src, width: img.width, height: img.height, ...getImagePosition(img,
            [reportContainerRef.current.getBoundingClientRect().top, ...pageBreaks],
            reportContainerRef.current.getBoundingClientRect().left
          )
        })
      ))
      if (!lastPageFooter)
        reportFooterRef.current.style.display = 'none'
      compactHeaderContainerRef.current.style.display = 'none'
      reportContainerRef.current.style.height = 'auto'
      if (!isPdfPrinter) {
        resize()
      }
    }
    if (isPdfPrinter) {
      document.fonts.ready.then(() => createLayout())
    } else {
      createLayout()
    }
  },[imagesLoaded,addendums])

  useEffect(() => {
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])

  useEffect(() => {
    const prevScroll = window.pageYOffset
    window.scroll(0, prevScroll)
    if (prevAddendums && prevAddendums !== addendums) {
      const headers = addendumsContainerRef.current.querySelectorAll(".page-break")
      smoothScroll({offset:-50,target:headers[headers.length - 1]})
    }
  })


  return (
    <PageResizerContainer isPdfPrinter={isPdfPrinter}>
      <div ref={reportContainerRef} className="report-page">

        {reportKeyImages.filter(_ => _.uploadStatus).length > 0 &&
        <Fill name="Notifications">
          <div className="system-notification with-loader light">
            <span>There are key images still uploading...</span>
          </div>
        </Fill>
        }
        <div ref={headerContainerRef} className={'report-header ' + layout.headerLayout}>
          <ReportHeaderView organization={organization} headerLayout={layout.headerLayout} getResource={getResource}/>
        </div>

        <div className="report-title-container">
          <h2 className="report-title">{reportTitle.title}</h2>
          <div className="report-date-container">
            {
              lang &&
              <span className="report-date">{
                finished ?
                  formatDate(reportTitle.finishedTime ? isoStrToDate(reportTitle.finishedTime) : new Date(lastSaved), lang, layout.dateFormat + ' - hh:mm') :
                  formatDate(new Date(lastSaved), lang, layout.dateFormat)
              }</span>
            }
          </div>
        </div>


        {
          !!reportBasicInfos.length &&
          <div className="report-basic-info">
            <div className="box report-basic-info-box">
              <div className="column-wrapper">
                {reportBasicInfos.filter((_, i) => (i + 1) % 2).map(info =>
                  <div className="report-basic-info-item" key={info.id}>
                    <label>{info.label}:</label> <span>{info.value}</span>
                  </div>
                )}
              </div>
              <div className="column-wrapper">
                {reportBasicInfos.filter((_, i) => i % 2).map(info =>
                  <div className="report-basic-info-item" key={info.id}>
                    <label>{info.label}:</label> <span>{info.value}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        }
        <div ref={fieldsContainerRef} className="report-fields-list">
          {reportFields.map(field =>
            <div className="report-field-item breakable-field next-page-if-min-small" key={field.id}>
              <div></div><label>{field.label}:</label>
              <RichTextDisplay className="field-content" content={field.value} getResource={getResource}/>
            </div>)}
        </div>

        <div className="report-signatures" ref={signaturesContainerRef}>
          {reportSignatures.filter(_ => availableSignatures[_.signatureId]).reduce(groupByTwo, []).map(responsibles =>
            <div key={responsibles[0].id} className="drag-drop-limits double-signature-container field-content-block">
              {responsibles.map(responsible =>
                <div key={responsible.id} className={'signature-container ' + responsible.structure}>
                  <ReportSignatureView
                    responsible={responsible}
                    getResource={getResource}
                    signature={availableSignatures[responsible.signatureId]}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="report-compact-header" ref={compactHeaderContainerRef}>
          <div className="report-compact-header-content">
            <span>{organization.organizationName}</span>
            <span>Patient: {patientName}</span>
          </div>
          <div className="report-compact-header-margin"/>
        </div>

        <div className="footer-filler" ref={footerFillerRef}>
        <div className="report-footer" ref={reportFooterRef}>
          <div className="organization-details">
            {organization.organizationName.trim() &&
            <span className="organization-name">{organization.organizationName}</span>}
            <div className="address-details">
              {organization.address.trim() && <span className="address">{organization.address} </span>}
              {organization.city.trim() && <span className="city">{organization.city}</span>}
              {organization.state.trim() && <span className="state"> {organization.state}</span>}
              {organization.zipCode.trim() && <span className="zipcode">{organization.zipCode}</span>}
              {organization.country.trim() && <span className="country">{organization.country} </span>}
            </div>
            {organization.phoneNumber.trim() && <span className="phone">Phone: {organization.phoneNumber} </span>}
            {organization.faxNumber.trim() && <span className="fax">Fax: {organization.faxNumber} </span>}
            {organization.email.trim() && <span className="email">Email: {organization.email} </span>}
          </div>
        </div>
        </div>


        <div className={"report-key-images-container"} ref={reportKeyImagesContainerRef}>
          {!hideKeyImages && reportKeyImages.map((keyImage, i) =>
            <>{i === 0 && <div className="always-break-here"/>}
            <div className="report-key-image-container field-content-block" key={keyImage.id}>
              <div className="image-container">
                <KeyImagePrint
                  crossOrigin="anonymous"
                  height={keyImage.imageHeight}
                  width={keyImage.imageWidth}
                  paperWidth={paperSize[0] * 96}
                  src={getBackgroundKeyImage(keyImage, getResource)}
                />
              </div>
              <div className="text-container">
                <div className="report-key-image-label">{getKeyImageNameOrPlaceholder(keyImage, i)}</div>
                {keyImage.description.trim() &&
                <div className="report-key-image-description">{keyImage.description}</div>}
              </div>
            </div>
            </>)}
        </div>
        <div className="last-page-filler" ref={lastPageFillerRef }/>

        <div className="addendum-pages" ref={addendumsContainerRef}>
          {
            addendums?.map((addendum, i) =>
              <div key={addendum.content} className="addendum-container">
                <div>
                  <div className="breakable-field">
                    <div>
                      {
                        (addendums.length - 1) === i && editAddendum
                        ? (!isEditingAddendum && !sharedReportView && <Button className="addendum-page-action" small>Edit</Button>)
                        : <span className="addendum-page-action not-editable">This addendum can no longer be edited.</span>
                      }
                      <div className="title">Addendum #{i + 1}</div>
                      <div className="last-saved">{formatDate(isoStrToDate(addendum.lastSavedStr || addendum.lastSaved), lang, layout.dateFormat + ' - hh:mm')}</div>
                    </div>
                    <RichTextDisplay content={addendum.content} getResource={getResource}/>
                  </div>
                  {addendum.signatures.length>0 && <div className="report-signatures">
                    {(addendum.signatures||[]).filter(_ => availableSignatures[_.signatureId]).reduce(groupByTwo, []).map(responsibles =>
                      <div key={responsibles[0].id} className="drag-drop-limits double-signature-container field-content-block">
                        {responsibles.map(responsible =>
                          <div key={responsible.id} className={'signature-container ' + responsible.structure}>
                            <ReportSignatureView
                              responsible={responsible}
                              getResource={getResource}
                              signature={availableSignatures[responsible.signatureId]}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>}
                </div>
                <div className={`addendums-last-page-filler ${i === addendums.length -1 ? "addendum-last-page-filler" : ""}`}/>
              </div>
            )
          }
        </div>
          <div className="report-page-footer" ref={reportPaginationPageFooterRef}/>
      </div>
    </PageResizerContainer>
  )
}

function groupByTwo(acc, item, i) {
  return [
    ...acc.slice(0, i / 2),
    (i % 2 ?
        [...acc[Math.floor(i / 2)], item] :
        [item]
    )
  ]
}
